/**
 * Presentation demo: sessionless dashboard access (cookie + middleware bypass for dashboard routes).
 * Production: set NEXT_PUBLIC_ENABLE_PRESENTATION_DEMO=true (review before public prod).
 * Development: enabled automatically when NODE_ENV === 'development'.
 */

import {
  DEMO_INDIVIDUAL_DISPLAY_NAME,
  DEMO_INDIVIDUAL_EMAIL,
  DEMO_INSTITUTION_DISPLAY_NAME,
  DEMO_INSTITUTION_EMAIL,
} from '@/lib/demo-content';

export const PRESENTATION_DEMO_COOKIE = 'ertzyx-presentation-demo';

export const PRESENTATION_DEMO_INDIVIDUAL_USER_ID = 'f0000001-0000-4000-8000-000000000001';
export const PRESENTATION_DEMO_INSTITUTION_USER_ID = 'f0000002-0000-4000-8000-000000000001';

export type PresentationDemoRole = 'individual' | 'institution';

export function isPresentationDemoFeatureEnabled(): boolean {
  if (process.env.NEXT_PUBLIC_ENABLE_PRESENTATION_DEMO === 'true') {
    return true;
  }
  // Local dev: demo entry points work without editing .env (middleware uses same helper).
  return process.env.NODE_ENV === 'development';
}

export function isPresentationDemoUserId(id: string | undefined | null): boolean {
  if (!id) return false;
  return (
    id === PRESENTATION_DEMO_INDIVIDUAL_USER_ID || id === PRESENTATION_DEMO_INSTITUTION_USER_ID
  );
}

const nowIso = () => new Date().toISOString();

/** Shape matches UserProfile in AuthContext. */
export function buildPresentationDemoProfile(role: PresentationDemoRole) {
  if (role === 'individual') {
    return {
      id: PRESENTATION_DEMO_INDIVIDUAL_USER_ID,
      email: DEMO_INDIVIDUAL_EMAIL,
      full_name: DEMO_INDIVIDUAL_DISPLAY_NAME,
      avatar_url: '/demo-avatars/sophie-lin.png',
      role: 'individual',
      institution_name: '',
      institution_id: '',
      vault_id: 'demo-vault-individual',
      is_active: true,
      created_at: nowIso(),
      subscription_tier: 'free',
      profile_setup_complete: true,
    };
  }
  return {
    id: PRESENTATION_DEMO_INSTITUTION_USER_ID,
    email: DEMO_INSTITUTION_EMAIL,
    full_name: DEMO_INSTITUTION_DISPLAY_NAME,
    avatar_url: '',
    role: 'institution',
    institution_name: DEMO_INSTITUTION_DISPLAY_NAME,
    institution_id: 'demo-institution',
    vault_id: 'demo-vault-institution',
    is_active: true,
    created_at: nowIso(),
    subscription_tier: 'free',
    profile_setup_complete: true,
  };
}

export function buildPresentationDemoUser(role: PresentationDemoRole) {
  if (role === 'individual') {
    return {
      id: PRESENTATION_DEMO_INDIVIDUAL_USER_ID,
      email: DEMO_INDIVIDUAL_EMAIL,
      aud: 'authenticated',
      role: 'authenticated',
      app_metadata: {},
      user_metadata: {
        role: 'individual',
        full_name: DEMO_INDIVIDUAL_DISPLAY_NAME,
      },
      created_at: nowIso(),
    };
  }
  return {
    id: PRESENTATION_DEMO_INSTITUTION_USER_ID,
    email: DEMO_INSTITUTION_EMAIL,
    aud: 'authenticated',
    role: 'authenticated',
    app_metadata: {},
    user_metadata: {
      role: 'institution',
      full_name: DEMO_INSTITUTION_DISPLAY_NAME,
      institution_name: DEMO_INSTITUTION_DISPLAY_NAME,
    },
    created_at: nowIso(),
  };
}
